/**
 * Arrows for the Gann confluence scan: days where a trine and a hard aspect meet on a shared body.
 *
 * <p>Pure functions over a scan response and the chart's own dates. The snapping is the part worth
 * testing on its own — a confluence falls on a calendar day, and the chart is indexed by trading
 * days, so most of them land on a date the chart does not have.
 */

// PENDING_COLOR is re-exported below but no longer USED here: a forward column's mark is a
// neutral triangle on the rail now, and the gold circle it used to take survives one level down,
// in the renderer, as the fallback for such a mark drawn at a price. Callers and tests still
// reach it through this module, which is why the re-export stays.
import { ctsMarkStyle, ctsNeutralMarkStyle } from './ctsMarkStyle';
import { aspectLabelParts } from './visualUniverseAspects';
import { formatDelta } from './pivotCommonalities';
import { indexDigit, badgeText, markerTooltipLines } from './ctsPatterns';

/** The overlay id, so a re-run replaces the previous arrows rather than stacking on them. */
export const GANN_CONFLUENCE_OVERLAY_ID = 'gann-confluence-arrows';

/**
 * The three angles that can be the second half of a confluence.
 *
 * <p>Mirrors {@code GannConfluence.HARD}. Written out rather than derived as "anything that is not
 * a trine", because the day's aspect list is a longer list than the rule — it carries sextiles and
 * quincunxes for the multi-planet configurations — and a negative test would silently promote them.
 */
export const HARD_ANGLES = [0, 90, 180];

/**
 * The tolerance each of the two aspects is allowed, in degrees.
 *
 * <p>Seven, matching the wheel's Gann reading, so a day this marks is a day the wheel would show
 * the same configuration on.
 */
export const GANN_CONFLUENCE_ORB = 7;

/**
 * The ceiling on the two aspects' orbs ADDED, for a day to be drawn.
 *
 * <p>Without it the scan is not a marker. Measured over 2020-2024 with twelve bodies at an orb of
 * 7, <b>98.4% of days qualify</b> — 1797 of 1827 — because 66 body pairs and a Moon crossing every
 * angle each month make some trine and some hard aspect share a body almost always. Requiring both
 * to be tight restores the signal:
 *
 * <pre>
 *   combined orb ≤ 0.5°     31 days   1.7%
 *   combined orb ≤ 1°      121 days   6.6%
 *   combined orb ≤ 2°      417 days  22.8%
 *   combined orb ≤ 3°      835 days  45.7%
 *   no ceiling            1797 days  98.4%
 * </pre>
 *
 * <p>One degree, which is about two dozen marks a year — readable on a chart, and tight enough
 * that a mark means the two aspects were near exact rather than merely present. The unfiltered
 * count is reported alongside so the narrowing is visible rather than assumed.
 */
export const GANN_CONFLUENCE_MAX_COMBINED_ORB = 1;

/**
 * What the scan dialog will accept for each dial.
 *
 * <p>The orb floor is 0.5 rather than 0 because the scan samples once a day: an aspect required to
 * be exact to a hundredth of a degree is found by luck rather than by being there. The ceiling
 * matches what the backend clamps to, so a number the dialog offers is a number the scan will
 * actually use — a control that silently gets overruled is worse than one that stops.
 */
export const ORB_BOUNDS = { min: 0.5, max: 15 };
export const COMBINED_BOUNDS = { min: 0.25, max: 30 };

/* ────────────────────────────── CTS — Critical Timing Scan ────────────────────────────── */

/**
 * The orb the toolbar's CTS button runs at.
 *
 * <p>1.5 degrees, and it changes the honesty of the run rather than its output. Measured over
 * 2015-01-01..2026-12-31, at a combined ceiling of 1 degree:
 *
 * <pre>
 *   orb 7.0, combined 1.0   ->   376 days marked,  4337 qualifying (99% of all days)
 *   orb 1.5, combined 1.0   ->   376 days marked,  1347 qualifying (31%)
 * </pre>
 *
 * <p>The same 376 marks. At a combined ceiling of 1 the primary orb was never the binding
 * constraint — the sum already forced each aspect under a degree — so the 7 was reporting that
 * 99% of days "qualify", a statistic that said nothing. The drawn set is unchanged and the number
 * beside it now means something.
 */
export const CTS_ORB = 1.5;

/**
 * The combined ceiling as a fraction of the orb, so the dependent figure follows the primary one.
 *
 * <p>Two thirds, which at the 1.5 orb gives exactly 1.0 — the value the scan has been running at.
 * Deriving it rather than writing a second constant is what makes the pair move together: changing
 * the orb alone used to leave the ceiling behind, and since the ceiling is the selectivity knob,
 * the run would look retuned while being identical.
 */
export const CTS_COMBINED_RATIO = 2 / 3;

/** The combined-orb ceiling implied by an orb. Clamped to what the dialog and backend accept. */
export function ctsCombinedOrb(orb = CTS_ORB) {
  const raw = (Number.isFinite(orb) ? orb : CTS_ORB) * CTS_COMBINED_RATIO;
  return Math.min(COMBINED_BOUNDS.max, Math.max(COMBINED_BOUNDS.min, Math.round(raw * 100) / 100));
}

/**
 * The parameters a CTS run uses, for a given orb.
 *
 * <p>The ceiling is DERIVED rather than passed in, which is what makes the three toolbar buttons
 * three tolerances of one rule instead of three separately-tuned rules. Measured across 1801-2100:
 * orb 1 marks 4.4% of days, orb 2 marks 15.3%, orb 3 marks 29.6% - the same rule, loosened.
 */
export function ctsScanParams(orb = CTS_ORB) {
  const use = Number.isFinite(orb) ? orb : CTS_ORB;
  return { orb: use, maxCombinedOrb: ctsCombinedOrb(use) };
}

/**
 * How few bars must be on screen for the arrows to pulse.
 *
 * <p>Zoomed out to a 33-year chart the arrows are a dense band and a pulse would be noise across
 * the whole width. Zoomed in they are a handful of marks among candles that are now wide enough to
 * hide them, which is the case the animation is for. 250 bars is about a year of daily data.
 */
export const CTS_PULSE_MAX_VISIBLE_BARS = 250;

/** How many arrows one run may draw. */
export const MAX_ARROWS = 400;

/**
 * The closest two arrows may sit, in bars.
 *
 * <p>A confluence does not resolve on one day — the two aspects tighten, reach exact, and separate
 * again over several days, so a run of adjacent bars all qualify and all get an arrow. They are one
 * event reported four times, and the cluster reads as a region of interest rather than a date.
 *
 * <p>Three bars, so a mark means "here" rather than "somewhere in this week".
 */
export const MIN_ARROW_SEPARATION_BARS = 3;

/** How far either side to look when deciding whether a bar is at a swing low or a swing high. */
export const PIVOT_LOOKAROUND_BARS = 5;

/**
 * Keeps the tightest arrow in each cluster and drops the rest.
 *
 * <p>Greedy non-maximum suppression: take the tightest remaining, claim the bars within
 * {@code minBars} of it, repeat. Greedy rather than a left-to-right sweep because the sweep keeps
 * whichever came FIRST — so a marginal Monday would suppress an exact Wednesday, which is the
 * opposite of the intent. Working from the tightest outwards is the backtracking this needs: every
 * arrow that survives is the strongest in its neighbourhood by construction.
 *
 * @param {Array<{idx:number, day:object}>} entries
 * @param {number} minBars
 * @returns {{kept:Array, suppressed:number}}
 */
export function suppressAdjacent(entries, minBars = MIN_ARROW_SEPARATION_BARS) {
  const byTightness = [...entries]
    .sort((a, b) => (a.day.tightestOrb ?? Infinity) - (b.day.tightestOrb ?? Infinity));

  const kept = [];
  for (const e of byTightness) {
    // Linear against the kept set rather than the candidates: the kept set is small, and this way
    // a bar is compared only with arrows that actually survived.
    if (kept.some(k => Math.abs(k.idx - e.idx) < minBars)) continue;
    kept.push(e);
  }
  return { kept, suppressed: entries.length - kept.length };
}

/**
 * Which way the arrow should point at a bar: up from a low, down from a high.
 *
 * <p>An arrow under a swing low pointing up, and over a swing high pointing down, says what the
 * mark is about — the confluence landed where price turned. Drawn the same way everywhere it says
 * only that a date passed.
 *
 * <p>Three questions, in order of how much they claim:
 * <ol>
 *   <li><b>Is this bar the extreme</b> of its neighbourhood? Then it IS the turn.</li>
 *   <li><b>Is it adjacent to one?</b> A confluence a day or two off the exact low is still about
 *       that low, and demanding the exact bar would throw away most of the useful marks.</li>
 *   <li><b>Otherwise, where does it sit</b> in the local range? Nearer the bottom reads as a low.
 *       This is a weaker claim and is the reason `certainty` is reported alongside.</li>
 * </ol>
 *
 * @param {Array<number[]>} ohlc rows of [open, close, low, high]
 * @param {number} index
 * @param {number} lookAround bars either side
 * @returns {{direction:'up'|'down', certainty:'pivot'|'adjacent'|'position'}}
 */
export function pivotDirectionAt(ohlc, index, lookAround = PIVOT_LOOKAROUND_BARS) {
  const from = Math.max(0, index - lookAround);
  const to = Math.min(ohlc.length - 1, index + lookAround);

  let lowIdx = -1;
  let highIdx = -1;
  let lowest = Infinity;
  let highest = -Infinity;
  for (let i = from; i <= to; i++) {
    const bar = ohlc[i];
    if (!bar) continue;
    const lo = bar[2];
    const hi = bar[3];
    if (typeof lo === 'number' && lo < lowest) { lowest = lo; lowIdx = i; }
    if (typeof hi === 'number' && hi > highest) { highest = hi; highIdx = i; }
  }

  if (lowIdx === index) return { direction: 'up', certainty: 'pivot' };
  if (highIdx === index) return { direction: 'down', certainty: 'pivot' };

  // Adjacency, decided by whichever turn is NEARER rather than by testing the low first: checking
  // in a fixed order makes the answer depend on the order and not on the bars.
  const toLow = lowIdx < 0 ? Infinity : Math.abs(lowIdx - index);
  const toHigh = highIdx < 0 ? Infinity : Math.abs(highIdx - index);
  if (Math.min(toLow, toHigh) <= 2 && toLow !== toHigh) {
    return { direction: toLow < toHigh ? 'up' : 'down', certainty: 'adjacent' };
  }

  const bar = ohlc[index] || [];
  const close = typeof bar[1] === 'number' ? bar[1] : null;
  if (close === null || !Number.isFinite(lowest) || !Number.isFinite(highest) || highest === lowest) {
    // Nothing to read the position from. Up is the safer default: it sits below the candle, where
    // the price labels and most other studies are not.
    return { direction: 'up', certainty: 'position' };
  }
  const where = (close - lowest) / (highest - lowest);
  return { direction: where <= 0.5 ? 'up' : 'down', certainty: 'position' };
}

/**
 * The chart date nearest a calendar date, or null when the chart has nothing near it.
 *
 * <p>Nearest in BOTH directions, not the next trading day forward. A confluence on a Saturday sits
 * between Friday and Monday, and calling it Monday would move every weekend event a day and a half
 * later while leaving weekday events exact — a bias that would show up as a systematic lag rather
 * than as an error.
 *
 * <p>`dates` is assumed ascending, which is how the chart holds it, so this binary-searches rather
 * than scanning: a 45-year daily chart is ~11,000 bars and a scan can return hundreds of days.
 *
 * @param {string[]} dates ascending 'yyyy-MM-dd'
 * @param {string} target
 * @param {number} maxGapDays refuse to snap further than this
 */
export function nearestDateIndex(dates, target, maxGapDays = 5) {
  if (!Array.isArray(dates) || dates.length === 0 || !target) return -1;

  const t = Date.parse(target);
  if (Number.isNaN(t)) return -1;

  let lo = 0;
  let hi = dates.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (Date.parse(dates[mid]) < t) lo = mid + 1;
    else hi = mid;
  }

  // The binary search lands on the first bar at or after the target; its predecessor is the only
  // other candidate, so both are compared rather than assuming the later one is closer.
  let best = -1;
  let bestGap = Infinity;
  for (const i of [lo - 1, lo]) {
    if (i < 0 || i >= dates.length) continue;
    const gap = Math.abs(Date.parse(dates[i]) - t);
    if (gap < bestGap) { bestGap = gap; best = i; }
  }

  const days = bestGap / 86400000;
  // A gap larger than a long weekend means the chart simply does not cover that date — a
  // confluence a year past the last bar must not be drawn on the last bar, where it would read as
  // a signal about today.
  return days <= maxGapDays ? best : -1;
}

/**
 * One arrow per qualifying day, snapped to the nearest bar.
 *
 * <p>Days that snap to the same bar are merged: a Saturday and a Sunday confluence both resolve to
 * the Friday, and two arrows on one bar would read as two independent findings about that bar. The
 * merged marker carries the strongest (tightest) of them and says how many it stands for.
 *
 * @param {Array<{date:string, count:number, tightestOrb:number, hits:Array}>} days
 * @param {{dates:string[], ohlc:Array<number[]>}} barData ohlc is [open, close, low, high]
 * @returns {{points:Array, snapped:number, offChart:number, merged:number, truncated:number}}
 */
/**
 * The colour a FORWARD confluence takes — re-exported, not restated.
 *
 * <p>This file used to hold its own `#d4a017` beside a comment claiming the colour was "shared with
 * the Integral Delta pending markers". It was shared only in the sense that two files happened to
 * contain the same literal, so brightening one would have left half the chart's pending marks in
 * the old gold. See `pendingSignals.js`, which owns it.
 */
export { PENDING_COLOR } from './pendingSignals';

/**
 * @param days    the scan's confluence days
 * @param barData the series the CHART renders — padded, deliberately: the scan reaches a year past
 *                the last real bar and those dates exist only on the padded axis
 * @param opts    `{lastRealDate}` — the newest TRADED bar. Everything after it is projection, and
 *                without this they cannot be told apart, because a padded column's date looks
 *                exactly like a traded one.
 */
export function buildGannArrowPoints(days, barData, { lastRealDate, aspectBreakdown } = {}) {
  const dates = barData && Array.isArray(barData.dates) ? barData.dates : [];
  const ohlc = barData && Array.isArray(barData.ohlc) ? barData.ohlc : [];
  const list = Array.isArray(days) ? days : [];

  const byIndex = new Map();
  let offChart = 0;

  list.forEach((day) => {
    const idx = nearestDateIndex(dates, day.date);
    if (idx < 0) { offChart++; return; }

    const existing = byIndex.get(idx);
    if (!existing) {
      byIndex.set(idx, { idx, day, mergedFrom: 1 });
      return;
    }
    existing.mergedFrom += 1;
    // The tightest wins the marker. Comparing on tightestOrb rather than on count because a day
    // with one exact configuration is a stronger reading than one with six loose ones.
    if ((day.tightestOrb ?? Infinity) < (existing.day.tightestOrb ?? Infinity)) existing.day = day;
  });

  // A confluence tightens over several days, so a run of adjacent bars all qualify — one event,
  // reported four times. Keeping only the tightest of each cluster is what turns a shaded region
  // back into a date.
  const { kept: spread, suppressed } = suppressAdjacent([...byIndex.values()]);

  const ordered = spread
    .sort((a, b) => (a.day.tightestOrb ?? Infinity) - (b.day.tightestOrb ?? Infinity));

  const kept = ordered.slice(0, MAX_ARROWS);
  const truncated = ordered.length - kept.length;

  const points = kept
    .sort((a, b) => a.idx - b.idx)
    .map(({ idx, day }) => {
      const bar = ohlc[idx] || [];
      // Only the direction is read now. pivotDirectionAt still returns its certainty, and the
      // merge still counts how many days landed on this bar - both are used by the journal, which
      // is where the detail the floater used to carry now lives.
      const { direction } = pivotDirectionAt(ohlc, idx);
      const up = direction === 'up';

      // IS THIS COLUMN A PROJECTION?
      //
      // The overlay is drawn against the padded axis on purpose, and a padded column's OHLC is a
      // flat placeholder at the last real close. So a forward confluence had its direction read
      // off a bar that does not exist, its arrow pointed by that reading, and its anchor set to
      // the last close - which put every forward mark on one horizontal line of green and red
      // triangles in the empty space right of the price action.
      //
      // Green and red mean an OUTCOME. Nothing has happened on a date the market has not reached,
      // so a forward confluence takes the pending vocabulary instead: a gold circle, no rotation,
      // no direction claimed.
      const forward = Boolean(lastRealDate) && dates[idx] > lastRealDate;

      // Anchored on the extreme the arrow points AT: an up arrow sits under the low, a down arrow
      // over the high. Anchoring both on the low would leave a down arrow hanging in mid-candle,
      // pointing at nothing.
      const anchor = up ? bar[2] : bar[3];
      const price = typeof anchor === 'number' ? anchor : null;

      return {
        date: dates[idx],
        price,
        position: forward ? 'above' : (up ? 'below' : 'above'),
        // Named so the renderer styles from a fact rather than inferring one, and so a caller can
        // count them without re-deriving the test.
        //
        // ⚠ It no longer decides the STYLE for these points. Every forward mark has a null price
        // and so lands on the rail, where the renderer's rail rule outranks the pending one. The
        // flag stays because it is still TRUE of the point and other readers count it.
        ...(forward ? { placement: 'extended' } : {}),
        // THE FILL IS THE ONLY THING THAT VARIES between the three states, and the renderer picks
        // between the two pairs this supplies from whether the bar has a revealed price. A forward
        // column can carry no direction at all -- its would have been read off a placeholder bar --
        // so it takes the neutral style outright.
        ...(forward ? ctsNeutralMarkStyle() : ctsMarkStyle(up)),
        // NO canvas label. A full-chart run marks hundreds of bars, and a label on each one
        // collapses into an unreadable band across the price action - measured at 400 marks on a
        // 33-year SPY chart. The arrow says WHERE; the tooltip says what, on the one bar being
        // asked about.
        // THE DATE, AND NOTHING ELSE.
        //
        // The CONFLUENCE date rather than the bar's. When the two differ the arrow has been snapped
        // to a neighbouring bar, and the bar's own date is already readable off the axis under the
        // pointer - repeating it would make the floater say the one thing the reader can see
        // anyway, and lose the one thing they cannot.
        //
        // Everything else the heading carried (the side, the configuration count, the tightest orb,
        // how many days merged onto this bar) is still produced and still reported: the journal
        // block written on every run carries the run's whole statistics, and aspectLinesFor - the
        // hub-first grouping with harmonic formations leading - is unchanged and still printed
        // there. Removing a view is not a reason to lose what it was a view of.
        title: `${day.date}`,

        // The confluence index, written INSIDE the triangle. One character, so it fits where a word
        // could not, and it is the one thing about a mark that cannot be inferred from its position.
        ...(indexDigit(day) !== null ? { label: indexDigit(day), labelInside: true } : {}),
        // The configuration letters, outside the triangle on the side it points away from - with
        // the full breakdown on THEM rather than on the arrow.
        //
        // The arrow's own floater stays the date and nothing else, which is a deliberate and
        // separately-tested property: a full-chart run marks hundreds of bars and a card on each
        // arrow is what the date-only rule was introduced to prevent. The breakdown belongs to the
        // badge, which only exists on days that have something to break down.
        ...(badgeText(day) ? {
          badge: badgeText(day),
          badgeTooltipLines: markerTooltipLines(day, aspectLinesFor(day)),
        } : {}),
        /*
         * ⚠ AND ON THE ARROW ITSELF, FOR AN ADMINISTRATOR ONLY -- which reverses the rule stated
         * immediately above. See `arrowTooltipLines`: the date-only floater protects a reader of
         * the chart, and `aspectBreakdown` is false for them, so their card is unchanged. An admin
         * who has just run the scan is who the breakdown is for, and on the badge it was a 10px
         * chip to find.
         *
         * Gated HERE rather than at the renderer, so a customer's chart option never contains the
         * breakdown at all -- there is nothing to withhold at draw time.
         */
        ...(() => {
          // THE REASON on the card, EVERYTHING ELSE behind the disclosure. `studyLines` is the
          // source the odds effect recomposes the expanded half from, and it never writes it --
          // see `composeMarkCard`.
          const reason = arrowTooltipLines(day, { aspectBreakdown });
          const detail = arrowDetailLines(day, { aspectBreakdown });
          return {
            ...(reason.length > 0 ? { tooltipLines: reason } : {}),
            ...(detail.length > 0 ? { studyLines: detail, tooltipMoreLines: detail } : {}),
          };
        })(),
      };
    })
    .filter(p => typeof p.price === 'number');

  return {
    points,
    snapped: points.length,
    offChart,
    merged: byIndex.size,
    suppressed,
    truncated,
    up: points.filter(p => p.position === 'below').length,
    down: points.filter(p => p.position === 'above').length,
  };
}

/**
 * The day's aspects, grouped around the planet that holds both halves of a confluence.
 *
 * <h2>The hub is the reading</h2>
 *
 * What the study looks for is one planet carrying a trine AND a conjunction, square or opposition.
 * That planet is the hub, and the two aspects are one statement about it — so both are written
 * with the hub FIRST, whatever order the bodies would normally take, and the pair is kept together:
 *
 * <pre>
 *   Mo 120°  (0°) Pl        Moon is the hub: trine Pluto,
 *   Mo  90°  (1°) Ma        and square Mars
 *
 *   Me 120°  (1°) Ra        a second hub
 *   Me  90°  (3°) Ne
 *
 *   Ju 180°  (2°) Sa        lone aspects, at the tail
 * </pre>
 *
 * <p>Writing the hub first is not cosmetic. These angles are folded to [0,180], so an aspect is
 * symmetric — "Mo 120 Pl" and "Pl 120 Mo" are the same fact — and the natural body order would put
 * the hub second in one line and first in the next, which is exactly what makes a shared planet
 * hard to see. Leading with it is what makes the two lines legible as one configuration.
 *
 * <p>Groups are ordered by their tightest pairing, so the strongest configuration leads. Within a
 * group the trines come before the hard aspects; a group never repeats an aspect another group
 * already claimed, so a trine shared by two hubs is listed once, under the stronger.
 *
 * <p>Lone aspects are those standing that day that are part of no pairing at all. They come last,
 * after a blank line, because the mark is not about them — but omitting them would describe the day
 * as quieter than it was.
 *
 * <p>Every line is built through {@link aspectLabelParts}, the same function the wheel's summary
 * panel uses, so the two readings line up character for character.
 */
/**
 * THE TRIANGLE'S OWN HOVER CARD -- what created this mark, and what is coming behind it.
 *
 * <h2>⚠ A REVERSAL of a deliberate, separately-tested decision</h2>
 *
 * The arrow's floater was the DATE AND NOTHING ELSE, and that was not an oversight: a full-chart
 * run marks hundreds of bars, and a card opening on every pointer pass across the price action was
 * the thing the date-only rule was introduced to prevent. The breakdown lived on the BADGE, which
 * exists only on days that have something to break down.
 *
 * <p>What makes the reversal defensible is the ADMIN GATE, not a change of mind about clutter. The
 * date-only rule protects a reader of the chart; `aspectBreakdown` is false for them, so their
 * card is exactly the date it always was. An administrator who has just run a CTS scan is the one
 * audience the breakdown is for, and reaching it on the badge meant finding a 10px chip.
 *
 * <h2>It composes rather than computes</h2>
 *
 * `markerTooltipLines(day, aspectLinesFor(day))` is the SAME content the badge has always carried
 * -- the hub-first grouping with harmonic formations leading. A second builder would be a second
 * answer to "which aspects made this triangle", and the two would agree on the day they were
 * written and never again.
 *
 * @param day the classified confluence day, or null when the classification was unavailable
 * @param aspectBreakdown true only for an administrator; false yields no aspect lines at all
 * @param clusterLine the cooldown sentence, or null when nothing was suppressed
 * @returns the lines, or an EMPTY ARRAY when there is nothing to add to the date
 */
export function arrowTooltipLines(day, { aspectBreakdown = false } = {}) {
  /*
   * ⚠ THE REASON ALONE, which is a NARROWING of what this returned an hour ago.
   *
   * It returned the full description -- the index heading, every standing aspect, the pattern
   * letters -- and with the odds card composed below it the result was twelve lines. Reported as
   * "very verbose and for many bars either the top or the bottom parts are unreadable", which is
   * what a card taller than the space above or below the mark does.
   *
   * Everything else is still reachable: `arrowDetailLines` carries it and the renderer puts it
   * behind a disclosure. Nothing was deleted; the DEFAULT changed.
   */
  return aspectBreakdown ? reasonLines(day) : [];
}

/**
 * EVERYTHING ELSE about the mark, for the expander.
 *
 * <p>The full description — index, every standing aspect grouped hub-first, the configuration
 * letters — plus the cluster column's sentence. The reason lines appear here too, inside the
 * grouping: this is the complete picture rather than a remainder, so a reader who opens it is not
 * reading half a card.
 */
export function arrowDetailLines(day, { aspectBreakdown = false, clusterLine = null } = {}) {
  const full = aspectBreakdown ? markerTooltipLines(day, aspectLinesFor(day)) : [];
  /*
   * ⚠ EVERY formation, when the day carries more than one.
   *
   * The collapsed card shows the tightest alone, so a day with four pairings would otherwise lose
   * three of them: their individual aspects are all in the standing list below, but WHICH pairs
   * are formations is exactly what that list does not say — the `▸` marker beside it means
   * something else (a same-displayed-orb coincidence), so it cannot be read as the answer.
   *
   * Headed, and only when it says something the single line above does not.
   */
  const every = aspectBreakdown ? reasonLines(day, { all: true }) : [];
  const extra = every.length > 2
    ? [`${(day.hits || []).length} formations`, '', ...every] : [];
  const head = extra.length > 0 && full.length > 0 ? [...extra, ''] : extra;
  const body = [...head, ...full];
  if (!clusterLine) return body;
  return body.length > 0 ? [...body, '', clusterLine] : [clusterLine];
}

/**
 * THE WHOLE CARD: what made this mark, then what the odds of it are.
 *
 * <h2>⚠ WHY THIS IS A FUNCTION AND NOT A `concat` AT THE CALL SITE</h2>
 *
 * The odds are attached by an EFFECT that reads the overlay it writes -- it finds the CTS overlay
 * in `overlaysRef.current`, maps its points and writes them back. So it runs again whenever a
 * cycle is drawn or the replay steps, and each run sees the points it last wrote.
 *
 * Appending to `tooltipLines` there would therefore APPEND AGAIN EVERY TIME, and the card would
 * grow without bound -- a defect that only appears after the second re-render and reads as a
 * runaway tooltip rather than as a composition mistake.
 *
 * <p>So the study's own description lives in `studyLines`, which the effect never writes, and
 * `tooltipMoreLines` -- the EXPANDED half -- is REBUILT from it each run. That makes the
 * composition idempotent by construction rather than by the effect remembering not to append.
 *
 * <p>The collapsed card (`tooltipLines`, the reason) is left alone entirely: it is short by
 * design and the odds have no business in it.
 *
 * <h2>Order: what the mark IS, before what it is worth</h2>
 *
 * The aspects identify the mark and are knowable from the sky alone; the odds are an estimate over
 * precedents. Asked for in that order, too -- the odds card was already here and was the one the
 * reader kept getting instead of the breakdown they asked for.
 *
 * @param studyLines the study's own description -- aspects, and the cluster column's sentence
 * @param oddsLines the odds card, or empty when this mark has no record
 */
export function composeMarkCard(studyLines, oddsLines) {
  const study = Array.isArray(studyLines) ? studyLines : [];
  const odds = Array.isArray(oddsLines) ? oddsLines : [];
  if (study.length === 0) return odds;
  if (odds.length === 0) return study;
  // A blank line between them, and only when both are present -- two different questions run
  // together read as one list.
  return [...study, '', ...odds];
}

/**
 * ONE ASPECT, as a column-aligned line: `Ju 120°  (0°03') Ma`.
 *
 * <p>Module level so the REASON and the full standing list cannot format differently — two
 * layouts of one fact in one card is worse than either.
 *
 * <h2>The orb in degrees and arcminutes, which `aspectLabelParts` cannot give</h2>
 *
 * That function rounds to WHOLE DEGREES — right for the Visual Universe wheel, where an orb runs
 * to eight and the integer is the reading. For CTS it is information-free: the combined ceiling is
 * two thirds of a degree, so every orb in this study is under 1 and every line printed `(0°)`. A
 * column saying the same thing on every row of every card is worse than an absent one, because it
 * looks like a measurement. `formatDelta` is the pivot study's own formatter, carrying the
 * identical argument; `aspectLabelParts` is deliberately NOT changed, because the wheel reads it.
 */
export function aspectLine(asp) {
  const p = aspectLabelParts(asp);
  return `${p.p1} ${p.angle} ${`(${formatDelta(asp.orb)})`.padStart(8)} ${p.p2}`;
}

/**
 * WHY THIS BAR HAS A TRIANGLE — and nothing else.
 *
 * <p>The trine, and the hard aspect (0 / 90 / 180) it shares a body with. That pair IS the CTS
 * rule, so these lines are the whole answer to the only question a mark raises.
 *
 * <h2>⚠ BUILT FROM `day.hits`, NOT BY FILTERING THE STANDING ASPECT LIST</h2>
 *
 * Each hit carries its own `trine` and `hard`, which is the rule's own output. The alternative —
 * picking the formation out of `aspectLinesFor`'s grouped lines — would be picking out the `▸`
 * marker, and that marker means something ELSE: it flags a hub whose two aspects share the same
 * DISPLAYED orb, a coincidence finding. On 2024-01-02 and 2024-01-13 the real formation carries no
 * marker at all, so a filter would have returned nothing for exactly the days it was built for.
 *
 * <h2>The hub leads both lines</h2>
 *
 * So the shared body reads down the left edge, which is what shows the two aspects are one
 * configuration rather than two that happen to be in orb today.
 *
 * @param day the classified day, or null when the classification was unavailable
 * @returns the lines, or EMPTY when the day carries no hit
 */
export function reasonLines(day, { all = false } = {}) {
  /*
   * ⚠ ONE FORMATION BY DEFAULT — the day's TIGHTEST, which is `hits[0]`.
   *
   * Found in a browser: a crowded day produces up to four pairings (the server caps it there), and
   * rendering every one put ELEVEN lines back on the card — the verbosity this change exists to
   * remove, arriving by a different route.
   *
   * The tightest is not merely the shortest answer, it is the RIGHT one: `GannConfluenceService`
   * takes `confluenceIndex` and `tightestOrb` from `hits[0]`, so the digit drawn inside the
   * triangle describes that pairing and no other. A card leading with a different formation would
   * disagree with the number it hangs off.
   *
   * `{ all: true }` is what the disclosure uses, so nothing is lost.
   */
  const every = Array.isArray(day && day.hits) ? day.hits : [];
  const hits = all ? every : every.slice(0, 1);
  const out = [];
  const seen = new Set();
  hits.forEach((hit) => {
    if (!hit || !hit.trine || !hit.hard) return;
    /*
     * The shared body, which is what makes the pair a formation rather than two coincidences.
     *
     * TAKEN FROM THE RESPONSE, which states it (`Hit.sharedBody`), and derived only as a fallback.
     * Deriving it here would be a second answer to a question the rule has already answered — and
     * the derivation has a real failure mode the server's value does not: a pair sharing BOTH
     * bodies (a trine and a hard aspect between the same two planets, which the folded separation
     * cannot produce today but nothing in this function knows that) would pick whichever came
     * first in the array.
     */
    const shared = hit.sharedBody
      || [hit.trine.a, hit.trine.b].find(b => b === hit.hard.a || b === hit.hard.b)
      || null;
    const lead = (asp) => (shared && asp.b === shared ? { ...asp, a: asp.b, b: asp.a } : asp);
    const pair = [lead(hit.trine), lead(hit.hard)].map(aspectLine);
    // Deduped on the rendered pair: two hits can resolve to the same two lines, and a card that
    // printed them twice would read as two findings.
    const k = pair.join('|');
    if (seen.has(k)) return;
    seen.add(k);
    if (out.length > 0) out.push('');
    out.push(...pair);
  });
  return out;
}

export function aspectLinesFor(day) {
  const hits = Array.isArray(day && day.hits) ? day.hits : [];
  const standing = Array.isArray(day && day.aspects) && day.aspects.length > 0
    ? day.aspects
    // Older responses carried only the pairings. Falling back to them keeps a stale backend
    // readable, at the cost of the lone tail, which those responses simply do not contain.
    : hits.flatMap(h => [h.trine, h.hard]).filter(Boolean);

  // Keyed on the unordered pair and the angle, so the same aspect reached from either side is one
  // aspect however many times it is named.
  const key = asp => [asp.a, asp.b].sort().join('|') + '|' + asp.angle;

  const unique = new Map();
  standing.forEach((asp) => {
    if (!asp) return;
    const k = key(asp);
    const prev = unique.get(k);
    if (!prev || asp.orb < prev.orb) unique.set(k, asp);
  });
  const aspects = [...unique.values()];

  /** The aspect rewritten so `hub` leads. Safe because a folded aspect is symmetric. */
  const leadWith = (asp, hub) => (asp.a === hub ? asp : { ...asp, a: asp.b, b: asp.a });

  const line = aspectLine;
  const byOrb = (x, y) => x.orb - y.orb;

  // ---- the harmonic formation ------------------------------------------------------------------
  //
  // A hub holding a trine and a hard aspect at the SAME displayed orb - "Ra 120 Me (0deg)" beside
  // "Ra 0 Ma (0deg)". The coincidence is the finding: two angles closing on one body at once is a
  // different event from two that merely both happen to be within orb today.
  //
  // Compared on the ROUNDED orb, because that is the number on screen. Comparing the exact orbs
  // would find almost nothing - two doubles agreeing to 15 places is chance, not configuration -
  // and would mark pairs the reader sees as identical while leaving identical-looking ones unmarked.
  const shownOrb = asp => Math.round(asp.orb);

  const harmonicPair = (trines, hard) => {
    let found = null;
    trines.forEach((t) => {
      hard.forEach((h) => {
        if (shownOrb(t) !== shownOrb(h)) return;
        // Tightest displayed orb wins; exact orbs break the tie, so a pair at a true 0.1 outranks
        // one at 0.4 when both print as 0.
        const cand = { trine: t, hard: h, shown: shownOrb(t), exact: t.orb + h.orb };
        if (!found || cand.shown < found.shown
            || (cand.shown === found.shown && cand.exact < found.exact)) found = cand;
      });
    });
    return found;
  };

  // Two columns wide on every line, marked or not, so the marker cannot shift the aligned fields
  // out of their columns - which is the whole reason the tooltip is monospace.
  const MARK = '▸ ';
  const PAD = '  ';

  // ---- hubs, derived from the aspects themselves ----------------------------------------------
  //
  // NOT from `hits`. The backend caps its pairings at four per day, so on a busy day a genuine hub
  // can be missing from them entirely — measured on 2024-01-03, Mercury's conjunction to Mars was
  // cut and then appeared in the lone tail, describing a body that holds a trine and a conjunction
  // as holding neither. The rule is the same rule; applying it to the complete list is what makes
  // the answer complete.
  //
  // "Hard" is the THREE angles the rule is built from, tested explicitly rather than as "not a
  // trine". The day's aspect list now also carries sextiles and quincunxes — they are what make a
  // kite, a mystic rectangle and a yod visible — and under the old negative test each of them
  // became a hard aspect. A body holding a trine and a sextile would have been reported as a hub,
  // and its two lines marked as a harmonic formation, which is a configuration this study does not
  // look for and would not have found.
  const bodies = new Map();
  aspects.forEach((asp) => {
    const isTrine = asp.angle === 120;
    const isHard = HARD_ANGLES.includes(asp.angle);
    if (!isTrine && !isHard) return;
    [asp.a, asp.b].forEach((name) => {
      if (!bodies.has(name)) bodies.set(name, { trines: [], hard: [] });
      (isTrine ? bodies.get(name).trines : bodies.get(name).hard).push(asp);
    });
  });

  const groups = [...bodies.entries()]
    .filter(([, g]) => g.trines.length > 0 && g.hard.length > 0)
    .map(([hub, g]) => {
      const bestTrine = Math.min(...g.trines.map(a => a.orb));
      const bestHard = Math.min(...g.hard.map(a => a.orb));
      return { hub, ...g, best: bestTrine + bestHard, harmonic: harmonicPair(g.trines, g.hard) };
    })
    .sort((a, b) => {
      // A harmonic formation outranks any ordinary hub, however tight the ordinary one is. That is
      // a deliberate reordering rather than a tiebreak: a hub whose trine is exact and whose square
      // is 6 out sums to 6 and would otherwise sit below one at 3+3, even though the 3+3 is the
      // configuration the study is looking for.
      if (!!a.harmonic !== !!b.harmonic) return a.harmonic ? -1 : 1;
      if (a.harmonic) {
        if (a.harmonic.shown !== b.harmonic.shown) return a.harmonic.shown - b.harmonic.shown;
        return a.harmonic.exact - b.harmonic.exact;
      }
      // Strongest configuration first, measured on the tightest PAIRING the hub can make rather
      // than on how many aspects it carries: one exact pair beats six loose ones.
      return a.best - b.best;
    });

  const claimed = new Set();
  const blocks = [];
  groups.forEach((g) => {
    const take = list => list.filter(a => !claimed.has(key(a))).sort(byOrb);
    const trines = take(g.trines);
    const hard = take(g.hard);
    // A hub whose aspects a stronger hub already took is not a second finding, only the same one
    // relabelled. It needs BOTH halves still free to stand on its own.
    if (trines.length === 0 || hard.length === 0) return;

    [...trines, ...hard].forEach(a => claimed.add(key(a)));

    // Recomputed from what this block actually GOT, not from what the hub holds. A stronger hub may
    // have taken one half of the pair, and marking a line that is no longer in the block - or worse,
    // marking one whose partner is not - would point at a coincidence the reader cannot see.
    const pair = harmonicPair(trines, hard);
    const marked = new Set(pair ? [key(pair.trine), key(pair.hard)] : []);
    // The matching pair leads its own block, so the two lines the marker joins are adjacent.
    const ordered = pair
      ? [pair.trine, ...trines.filter(a => a !== pair.trine),
         pair.hard, ...hard.filter(a => a !== pair.hard)]
      : [...trines, ...hard];

    blocks.push(ordered.map(a => (marked.has(key(a)) ? MARK : PAD) + line(leadWith(a, g.hub))));
  });

  // ---- whatever else was standing, at the tail -------------------------------------------------
  const lone = aspects.filter(a => !claimed.has(key(a))).sort(byOrb).map(a => PAD + line(a));
  if (lone.length > 0) blocks.push(lone);

  // The separator is an empty string, not a space: the renderer joins on <br/>, so an empty entry
  // is exactly one blank line, while a space leaves a line the reader cannot tell is deliberate.
  const out = [];
  blocks.forEach((b, i) => {
    if (i > 0) out.push('');
    out.push(...b);
  });
  return out;
}

/** Green, pointing up from a low. */
export const UP_COLOR = '#2e7d32';

/** Red, pointing down from a high. */
export const DOWN_COLOR = '#c62828';

/**
 * Kept for callers that grade a mark by how tight it is.
 *
 * <p>No longer used by the arrows themselves: colour now carries DIRECTION, which is a categorical
 * reading, and a scale that meant tightness in one place and direction in another would be two
 * meanings on one channel.
 */
export function colourForOrb(orb) {
  if (typeof orb !== 'number') return '#6a1b9a';
  if (orb <= 0.5) return '#b71c1c';
  if (orb <= 1.5) return '#e65100';
  if (orb <= 3) return '#f9a825';
  return '#7cb342';
}

const GLYPH = { 0: '☌', 90: '□', 180: '☍' };

/** The hard aspect's symbol: conjunction, square, opposition. */
export function hardGlyph(angle) {
  return GLYPH[angle] || `${angle}°`;
}

/** Two letters, matching the abbreviations the wheel and the summary panel already use. */
export function abbrev(name) {
  if (!name) return '?';
  const map = {
    Sun: 'Su', Moon: 'Mo', Mercury: 'Me', Venus: 'Ve', Mars: 'Ma', Jupiter: 'Ju',
    Saturn: 'Sa', Uranus: 'Ur', Neptune: 'Ne', Pluto: 'Pl', Rahu: 'Ra', Ketu: 'Ke',
  };
  return map[name] || String(name).slice(0, 2);
}
